import { getHttpClient } from '@/utilities/httpClients'
import { internalToValue } from '@/helpers'
import i18n from '@/i18n/SetupI18n'
import {
  API_GATEWAY_CLIENT,
  PRODUCT_TYPE_IX,
  PRODUCT_TYPE_MCR2,
  PRODUCT_TYPE_MEGAPORT,
  PRODUCT_TYPE_MVE,
  PRODUCT_TYPE_VXC,
} from '@/Globals'

/**
 * Generates notification options for pricebook request errors to be used with Element UI's toasts.
 * @param {string} productType Type of product that failed to fetch pricing for (e.g., 'MCR', 'MVE', 'VXC', etc.)
 */
export function generatePricebookNotificationOptions(productType) {
  return {
    title: i18n.t('pricebook.pricing-request-error'),
    message: i18n.t('pricebook.pricing-request-error-long', {
      productType: productType ?? i18n.t('general.unknown'),
    }),
    // Set to zero to require manual dismissal
    duration: 0,
  }
}

export const pricebook = {
  /**
   * NOTE: The prices returned include any discounts that are applied for that service or company.
   *
   * If you don't specify productUid or specify one that isn't valid, it will treat it as a new purchase.
   *
   * All of the below methods return a monthlyRate field
   */

  /**
   * @typedef {1 | 12 | 24 | 36} TermType
   */

  /****************************************/
  /*           Standard Requests          */
  /****************************************/

  /**
   * Get pricing information for ports
   * @param {Object} queryParams Parameters to define the port details pricing is requested for
   * @param {number} queryParams.locationId ID of the location the port is configured at
   * @param {number} queryParams.speed Speed of the configured port
   * @param {string} queryParams.currency Pricing currency
   * @param {TermType} [queryParams.term] Term of the contract in months (1, 12, 24, 36)
   * @param {boolean} [queryParams.crossConnectRequested] Whether a cross connect is requested for the port
   * @param {string} [queryParams.companyId] Used for custom pricing when inspecting a foreign quote. If not provided, it will fallback to the current user's company ID.
   *
   * @param {Object} [config] Request configuration
   *
   * @returns {Object} Pricing information for the port
   */
  async megaport(queryParams, config) {
    const { companyId, locationId, speed, currency, term = 1, crossConnectRequested } = internalToValue(queryParams)
    // Check if all required data has been passed first before calling pricebook
    if (!locationId || !speed || !currency) return

    // Only cross connect addon is supported for now
    const crossConnectAddOn = {
      addOnType: 'CROSS_CONNECT',
      crossConnectRequested: true,
    }

    const requestPayload = {
      productType: PRODUCT_TYPE_MEGAPORT,
      locationId,
      speed,
      term,
      currency,
      addOns: crossConnectRequested ? [crossConnectAddOn] : [],
    }

    try {
      const { data: pricing } = await getHttpClient(API_GATEWAY_CLIENT).post('v4/pricebook/product', requestPayload, {
        params: { companyId },
        ...internalToValue(config),
      })
      return pricing
    } catch (error) {
      console.error('Failed to load pricing data for MEGAPORT')
      throw error
    }
  },

  /**
   * Get pricing information for MCRs
   * @param {Object} queryParams Parameters to define the MCR details pricing is requested for
   * @param {number} queryParams.locationId ID of the location the MCR is configured at
   * @param {number} queryParams.speed Speed of the configured MCR
   * @param {string} queryParams.currency Pricing currency
   * @param {TermType} [queryParams.term] Term of the contract in months (1, 12, 24, 36)
   * @param {string} [queryParams.companyId] Used for custom pricing when inspecting a foreign quote. If not provided, it will fallback to the current user's company ID.
   *
   * @param {Object} [config] Request configuration
   *
   * @returns {Object} Pricing information for the MCR
   */
  async mcr(queryParams, config) {
    const { companyId, locationId, speed, term = 1, currency } = internalToValue(queryParams)
    // Check if all required data has been passed first before calling pricebook
    if (!locationId || !speed || !currency) return

    const requestPayload = {
      productType: PRODUCT_TYPE_MCR2,
      locationId,
      speed,
      term,
      currency,
    }

    try {
      const { data: pricing } = await getHttpClient(API_GATEWAY_CLIENT).post('v4/pricebook/product', requestPayload, {
        params: { companyId },
        ...internalToValue(config),
      })
      return pricing
    } catch (error) {
      console.error('Failed to load pricing data for MCR')
      throw error
    }
  },

  /**
   * Get pricing information for MVEs
   * @param {Object} queryParams Parameters to define the MVE details pricing is requested for
   * @param {number} queryParams.locationId ID of the location the MVE is configured at
   * @param {string} queryParams.vendor Vendor of the MVE
   * @param {string} queryParams.size Size of the MVE
   * @param {string} queryParams.currency Pricing currency
   * @param {TermType} [queryParams.term] Term of the contract in months (1, 12, 24, 36)
   * @param {string} [queryParams.companyId] Used for custom pricing when inspecting a foreign quote. If not provided, it will fallback to the current user's company ID.
   *
   * @param {Object} [config] Request configuration
   *
   * @returns {Object} Pricing information for the MVE
   */
  async mve(queryParams, config) {
    const { companyId, locationId, vendor, size, currency, term = 1 } = internalToValue(queryParams)
    // Check if all required data has been passed first before calling pricebook
    if (!locationId || !vendor || !size || !currency) return

    const requestPayload = {
      productType: PRODUCT_TYPE_MVE,
      locationId,
      vendor,
      size,
      term,
      currency,
    }

    try {
      const { data: pricing } = await getHttpClient(API_GATEWAY_CLIENT).post('v4/pricebook/product', requestPayload, {
        params: { companyId },
        ...internalToValue(config),
      })
      return pricing
    } catch (error) {
      console.error('Failed to load pricing data for MVE')
      throw error
    }
  },

  /**
   * Get pricing information for VXCs
   * @param {Object} queryParams Parameters to define the VXC details pricing is requested for
   * @param {number} queryParams.aLocationId ID of the location on the A-End of the VXC
   * @param {number} queryParams.bLocationId ID of the location on the B-End of the VXC
   * @param {number} queryParams.speed Speed of the configured VXC
   * @param {string} queryParams.aEndProductType Type of the A-End product
   * @param {string} queryParams.connectType Type of the VXC connection
   * @param {string} queryParams.currency Pricing currency
   * @param {TermType} [queryParams.term] Term of the contract in months (1, 12, 24, 36)
   * @param {string} [queryParams.companyId] Used for custom pricing when inspecting a foreign quote. If not provided, it will fallback to the current user's company ID.
   *
   * @param {Object} [config] Request configuration
   *
   * @returns {Object} Pricing information for the VXC
   */
  async vxc(queryParams, config) {
    const {
      companyId,
      aLocationId,
      bLocationId,
      speed,
      aEndProductType,
      connectType,
      currency,
      term = 1,
    } = internalToValue(queryParams)
    // Check if all required data has been passed first before calling pricebook
    if (!aLocationId || !bLocationId || !speed || !aEndProductType || !connectType || !currency) return

    const requestPayload = {
      productType: PRODUCT_TYPE_VXC,
      aLocationId,
      bLocationId,
      speed,
      term,
      aEndProductType,
      connectType,
      currency,
    }

    try {
      const { data: pricing } = await getHttpClient(API_GATEWAY_CLIENT).post('v4/pricebook/product', requestPayload, {
        params: { companyId },
        ...internalToValue(config),
      })

      return pricing
    } catch (error) {
      console.error('Failed to load pricing data for VXC')
      throw error
    }
  },

  /**
   * Get pricing information for IXs
   * @param {Object} queryParams Parameters to define the IX details pricing is requested for
   * @param {string} queryParams.ixType IX Exchange
   * @param {number} queryParams.portLocationId ID of the location the parent port is configured at
   * @param {number} queryParams.speed Speed of the configured IX
   * @param {string} queryParams.currency Pricing currency
   * @param {Object} [queryParams.companyId] Used for custom pricing when inspecting a foreign quote. If not provided, it will fallback to the current user's company ID.
   *
   * @param {Object} [config] Request configuration
   *
   * @returns {Object} Pricing information for the IX
   */
  async ix(queryParams, config) {
    const { companyId, ixType, portLocationId, speed, currency } = internalToValue(queryParams)
    // Check if all required data has been passed first before calling pricebook
    if (!ixType || !portLocationId || !speed || !currency) return

    const requestPayload = {
      productType: PRODUCT_TYPE_IX,
      ixType,
      portLocationId,
      speed,
      currency,
    }

    try {
      const { data: pricing } = await getHttpClient(API_GATEWAY_CLIENT).post('v4/pricebook/product', requestPayload, {
        params: { companyId },
        ...internalToValue(config),
      })

      return pricing
    } catch (error) {
      console.error('Failed to load pricing data for IX')
      throw error
    }
  },

  /*************************************/
  /*           Batch Requests          */
  /*************************************/

  /**
   * Get pricing information for VXCs for all terms
   * @param {Object} queryParams Parameters to define the VXC details pricing is requested for
   * @param {number} queryParams.aLocationId ID of the location on the A-End of the VXC
   * @param {number} queryParams.bLocationId ID of the location on the B-End of the VXC
   * @param {number} queryParams.speed Speed of the configured VXC
   * @param {string} queryParams.aEndProductType Type of the A-End product
   * @param {string} queryParams.connectType Type of the VXC connection
   * @param {string} queryParams.currency Pricing currency
   * @param {string} [queryParams.companyId] Used for custom pricing when inspecting a foreign quote. If not provided, it will fallback to the current user's company ID.
   *
   * @param {Object} [config] Request configuration
   *
   * @returns {Object} Pricing information for the VXC for all available terms
   */
  async vxcTerms(queryParams, config) {
    const { companyId, aLocationId, bLocationId, speed, aEndProductType, connectType, currency } =
      internalToValue(queryParams)

    // Check if all required data has been passed first before calling pricebook
    if (!aLocationId || !bLocationId || !speed || !aEndProductType || !connectType || !currency) return

    const requestPayload = {
      productType: PRODUCT_TYPE_VXC,
      aLocationId,
      bLocationId,
      speed,
      aEndProductType,
      connectType,
      currency,
    }

    try {
      const { data: pricing } = await getHttpClient(API_GATEWAY_CLIENT).post(
        'v4/pricebook/product/terms',
        requestPayload,
        {
          params: { companyId },
          ...internalToValue(config),
        },
      )

      return pricing
    } catch (error) {
      console.error('Failed to load termed pricing data for VXC')
      throw error
    }
  },

  /**
   * Get pricing information for ports for all terms
   * @param {Object} queryParams Parameters to define the port details pricing is requested for
   * @param {number} queryParams.locationId ID of the location the port is configured at
   * @param {number} queryParams.speed Speed of the configured port
   * @param {string} queryParams.currency Pricing currency
   * @param {boolean} [queryParams.crossConnectRequested] Whether a cross connect is requested for the port
   * @param {string} [queryParams.companyId] Used for custom pricing when inspecting a foreign quote. If not provided, it will fallback to the current user's company ID.
   *
   * @param {Object} [config] Request configuration
   *
   * @returns {Object} Pricing information for the port
   */
  async megaportTerms(queryParams, config) {
    const { companyId, locationId, speed, currency, crossConnectRequested } = internalToValue(queryParams)
    // Check if all required data has been passed first before calling pricebook
    if (!locationId || !speed || !currency) return

    // Only cross connect addon is supported for now
    const crossConnectAddOn = {
      addOnType: 'CROSS_CONNECT',
      crossConnectRequested: true,
    }

    const requestPayload = {
      productType: PRODUCT_TYPE_MEGAPORT,
      locationId,
      speed,
      currency,
      addOns: crossConnectRequested ? [crossConnectAddOn] : [],
    }

    try {
      const { data: pricing } = await getHttpClient(API_GATEWAY_CLIENT).post(
        'v4/pricebook/product/terms',
        requestPayload,
        {
          params: { companyId },
          ...internalToValue(config),
        },
      )
      return pricing
    } catch (error) {
      console.error('Failed to load pricing data for MEGAPORT')
      throw error
    }
  },

  /**
   * Get pricing information for MCRs for all terms
   * @param {Object} queryParams Parameters to define the MCR details pricing is requested for
   * @param {number} queryParams.locationId ID of the location the MCR is configured at
   * @param {number} queryParams.speed Speed of the configured MCR
   * @param {string} queryParams.currency Pricing currency
   * @param {string} [queryParams.companyId] Used for custom pricing when inspecting a foreign quote. If not provided, it will fallback to the current user's company ID.
   *
   * @param {Object} [config] Request configuration
   *
   * @returns {Object} Pricing information for the MCR
   */
  async mcrTerms(queryParams, config) {
    const { companyId, locationId, speed, currency } = internalToValue(queryParams)
    // Check if all required data has been passed first before calling pricebook
    if (!locationId || !speed || !currency) return

    const requestPayload = {
      productType: PRODUCT_TYPE_MCR2,
      locationId,
      speed,
      currency,
    }

    try {
      const { data: pricing } = await getHttpClient(API_GATEWAY_CLIENT).post(
        'v4/pricebook/product/terms',
        requestPayload,
        {
          params: { companyId },
          ...internalToValue(config),
        },
      )
      return pricing
    } catch (error) {
      console.error('Failed to load pricing data for MCR')
      throw error
    }
  },

  /**
   * Get pricing information for MVEs for all terms
   * @param {Object} queryParams Parameters to define the MVE details pricing is requested for
   * @param {number} queryParams.locationId ID of the location the MVE is configured at
   * @param {string} queryParams.vendor Vendor of the MVE
   * @param {string} queryParams.size Size of the MVE
   * @param {string} queryParams.currency Pricing currency
   * @param {string} [queryParams.companyId] Used for custom pricing when inspecting a foreign quote. If not provided, it will fallback to the current user's company ID.
   *
   * @param {Object} [config] Request configuration
   *
   * @returns {Object} Pricing information for the MVE
   */
  async mveTerms(queryParams, config) {
    const { companyId, locationId, vendor, size, currency } = internalToValue(queryParams)
    // Check if all required data has been passed first before calling pricebook
    if (!locationId || !vendor || !size || !currency) return

    const requestPayload = {
      productType: PRODUCT_TYPE_MVE,
      locationId,
      vendor,
      size,
      currency,
    }

    try {
      const { data: pricing } = await getHttpClient(API_GATEWAY_CLIENT).post(
        'v4/pricebook/product/terms',
        requestPayload,
        {
          params: { companyId },
          ...internalToValue(config),
        },
      )
      return pricing
    } catch (error) {
      console.error('Failed to load pricing data for MVE')
      throw error
    }
  },
}
